import { Component, type ComponentChildren } from 'preact'
import { captureError } from '../monitoring/errors'

type Props = {
  children: ComponentChildren
}

type State = {
  hasError: boolean
}

/**
 * Global Preact ErrorBoundary (minimal).
 * Captures render/lifecycle errors that may NOT reach window.onerror.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  componentDidCatch(err: any, info: any) {
    try {
      const message = (err && err.message) ? String(err.message) : 'Preact render error'
      const stack = (err && err.stack) ? String(err.stack) : null
      const componentStack = info && info.componentStack ? String(info.componentStack) : null
      captureError({
        type: 'preact_render_error',
        message,
        stack,
        severity: 'error',
        meta: { componentStack },
      })
    } catch {
      // never throw from monitoring
    }

    try {
      this.setState({ hasError: true })
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      // Keep it minimal; the app should remain usable even if a subtree crashed.
      return (
        <div style={{ padding: 16, color: 'var(--text)' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Something went wrong</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Please reopen the app.
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

